import type { SolarSystem } from "../sim/solar-system";
import type { ScaleMapper } from "./scale-mapper";

export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

export interface FrameData {
  count: number;
  focus: number;
  positions: Float32Array;
  radii: Float32Array;
  rotations: Float32Array;
  /** Eccentric anomaly per body (radians), as the simulation solved it. */
  anomalies: Float64Array;
  /** Julian date the positions are for. */
  jd: number;
  /** 1 when a body's orbit and label may be drawn; always 1 for textured and simple bodies. */
  shown: Uint8Array;
  /** Body visibility, also used for picking. */
  visible: Uint8Array;
  cameraPosition: Vec3Like;
  viewportHeight: number;
  fovRad: number;
  /** Device pixels per CSS pixel of the drawing buffer. */
  pixelRatio: number;
}

/** Maps heliocentric km into scene units, compressing each body's offset from its own parent. Doubles throughout. */
export function mapScene(
  parentIndex: Int32Array,
  positionsKm: Float64Array,
  radiiKm: Float32Array,
  count: number,
  mapper: ScaleMapper,
  outPositions: Float64Array,
  outRadii: Float32Array,
): void {
  for (let k = 0; k < count; k++) {
    const o = 3 * k;
    const p = parentIndex[k];
    outRadii[k] = mapper.mapRadius(radiiKm[k]);
    if (p < 0) {
      outPositions[o] = 0;
      outPositions[o + 1] = 0;
      outPositions[o + 2] = 0;
      continue;
    }
    const po = 3 * p;
    const dx = positionsKm[o] - positionsKm[po];
    const dy = positionsKm[o + 1] - positionsKm[po + 1];
    const dz = positionsKm[o + 2] - positionsKm[po + 2];
    const len = Math.hypot(dx, dy, dz);
    if (len === 0) {
      outPositions[o] = outPositions[po];
      outPositions[o + 1] = outPositions[po + 1];
      outPositions[o + 2] = outPositions[po + 2];
      continue;
    }
    const s = mapper.mapDistance(len) / len;
    outPositions[o] = outPositions[po] + dx * s;
    outPositions[o + 1] = outPositions[po + 1] + dy * s;
    outPositions[o + 2] = outPositions[po + 2] + dz * s;
  }
}

/** Subtracts the focus body's position so it sits at the origin, then narrows to float32 for the GPU. */
export function applyOrigin(
  scene: Float64Array,
  count: number,
  focus: number,
  out: Float32Array,
): void {
  const fx = scene[3 * focus],
    fy = scene[3 * focus + 1],
    fz = scene[3 * focus + 2];
  for (let k = 0; k < count; k++) {
    const o = 3 * k;
    out[o] = scene[o] - fx;
    out[o + 1] = scene[o + 1] - fy;
    out[o + 2] = scene[o + 2] - fz;
  }
}

/** Raises radii so no body renders smaller than minPixels. No-op for a degenerate viewport. */
export function clampRadii(
  positions: Float32Array,
  radii: Float32Array,
  count: number,
  cam: Vec3Like,
  minPixels: number,
  viewportHeight: number,
  fovRad: number,
): void {
  if (minPixels <= 0 || viewportHeight <= 0) return;
  const pixelsPerUnitAtOne = viewportHeight / 2 / Math.tan(fovRad / 2);
  for (let k = 0; k < count; k++) {
    const o = 3 * k;
    const dist = Math.hypot(
      positions[o] - cam.x,
      positions[o + 1] - cam.y,
      positions[o + 2] - cam.z,
    );
    if (dist === 0) continue;
    const minRadius = (minPixels * dist) / pixelsPerUnitAtOne;
    if (radii[k] < minRadius) radii[k] = minRadius;
  }
}

export class FrameUpdater {
  readonly data: FrameData;
  private readonly scene64: Float64Array;

  constructor(
    private readonly sim: SolarSystem,
    private readonly mapper: ScaleMapper,
  ) {
    this.scene64 = new Float64Array(3 * sim.count);
    this.data = {
      count: sim.count,
      focus: 0,
      positions: new Float32Array(3 * sim.count),
      radii: new Float32Array(sim.count),
      rotations: sim.rotations,
      anomalies: sim.anomalies,
      jd: sim.jd,
      shown: new Uint8Array(sim.count).fill(1),
      visible: new Uint8Array(sim.count).fill(1),
      cameraPosition: { x: 0, y: 0, z: 0 },
      viewportHeight: 1,
      fovRad: 1,
      pixelRatio: 1,
    };
  }

  update(focus: number, camera: Vec3Like, viewportHeight: number, fovRad: number): FrameData {
    const d = this.data;
    d.focus = focus;
    d.jd = this.sim.jd;
    d.cameraPosition = camera;
    d.viewportHeight = viewportHeight;
    d.fovRad = fovRad;
    mapScene(
      this.sim.table.parentIndex,
      this.sim.positions,
      this.sim.table.radiusKm,
      d.count,
      this.mapper,
      this.scene64,
      d.radii,
    );
    applyOrigin(this.scene64, d.count, focus, d.positions);
    clampRadii(
      d.positions,
      d.radii,
      d.count,
      camera,
      this.mapper.params.minPixels,
      viewportHeight,
      fovRad,
    );
    return d;
  }
}
